import React, { useState, useRef, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography, Box } from "@mui/material";

// --- Material Dashboard Pro React Components ---
import GridContainer from "components/Grid/GridContainer.js";
import GridItem from "components/Grid/GridItem.js";
import Card from "components/Card/Card.js";
import CardHeader from "components/Card/CardHeader.js";
import CardBody from "components/Card/CardBody.js";

// --- Styles from loginPageStyle.js for consistent look ---
import styles from "assets/jss/material-dashboard-pro-react/views/loginPageStyle.js";

const useStyles = makeStyles(styles);

export default function TermsOfUse() {
  const [cardAnimation, setCardAnimation] = useState("cardHidden");
  const isMountedRef = useRef(true);
  const classes = useStyles();

  // --- Card Animation Effect ---
  useEffect(() => {
    isMountedRef.current = true;
    const id = setTimeout(() => {
      if (isMountedRef.current) setCardAnimation("");
    }, 300);

    return () => {
      isMountedRef.current = false;
      clearTimeout(id);
    };
  }, []);

  return (
    <div className={classes.container}>
      <GridContainer justify="center">
        <GridItem xs={12} sm={10} md={8}>
          <Card login className={classes[cardAnimation]}>
            <CardHeader
              className={`${classes.cardHeader} ${classes.textCenter}`}
              color="rose"
            >
              <h4 className={classes.cardTitle}>Terms of Use</h4>
            </CardHeader>
            <CardBody>
              {/* --- Terms of Use Content Starts Here --- */}
              <Box sx={{ m: 0, p: 0 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Abiding by rules guarantees fair play and social order.
                </Typography>

                <Typography variant="body2" gutterBottom fontWeight="bold">
                  Last Update: 1st August 2025
                </Typography>

                <Box component="dl" mt={4}>
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (1). Accessing Beramour Application and Embedded Dating
                      Services.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Welcome to Beramour ("Beramour", "we", "our", or "us"). The
                    Beramour Dating Platform is a product of Beta Infinity Ltd.,
                    hereafter referred to as "the company". By accessing our
                    services—whether through a mobile device, tablet, computer,
                    or any other communication medium capable of presenting our
                    dating application—you agree to be bound by the following:
                    <ol type="a">
                      <li>Terms of Use,</li>
                      <li>Privacy Policy, and</li>
                      <li>Affiliate Policy.</li>
                    </ol>
                  </Typography>

                  <Typography component="dd" paragraph>
                    You will be deemed to have accepted these Terms of Use,
                    Privacy Policy, and Affiliate Policy by continuing to use
                    this website and the embedded application, regardless of
                    whether or not you have registered as a member.
                    <br />
                  </Typography>

                  <Typography component="dd" paragraph>
                    We may amend these Terms of Use, Privacy Policy, and
                    Affiliate Policy from time to time without prior notice. The
                    date of the last amendment is shown at the beginning of each
                    relevant document. You further agree that you are solely
                    responsible for ensuring that you regularly review the
                    current versions of these three agreements.
                    <br />
                  </Typography>

                  <Typography component="dd" paragraph>
                    If you do not agree with these Terms of Use, Privacy Policy,
                    and/or Affiliate Policy, you are requested to stop using the
                    embedded dating application and all its associated services
                    immediately.
                    <br />
                  </Typography>

                  {/* --- NEW: Support Center / Community Rules Incorporated --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (2). Support Center, Safety Tips, and Community Rules.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Our Support Center (including, without limitation, guidance
                    on account security, privacy, safety tips, referrals, and
                    community rules) forms part of how we operate the platform.
                    You agree to follow the Community Rules and safety guidance
                    as published within the application and/or Support Center.
                    Where there is a conflict between the Support Center
                    guidance and these Terms of Use, these Terms of Use shall
                    prevail.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(3). Entering into a Contractual Agreement.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    By registering as a Single or an Affiliate Member, you are
                    deemed to have read, understood, and agreed to the Terms of
                    Use, Privacy Policy, and/or Affiliate Policy as set by Beta
                    Infinity Ltd. in respect to the embedded application
                    (provided as the Beramour Dating Platform) and offering
                    services that may or may not be free of charge. After making
                    the required payment, you will have explicitly entered into
                    a binding contractual agreement with Beta Infinity Ltd. and
                    shall have agreed to be bound by all conditions set forth in
                    the Terms of Use, Privacy Policy, and/or Affiliate Policy.
                    <br />
                  </Typography>

                  {/* --- NEW: License to Use the App --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(4). Limited License and Acceptable Use.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Subject to your compliance with these Terms, Beramour grants
                    you a limited, revocable, non-exclusive, non-transferable
                    license to access and use the application for personal,
                    lawful dating and social connection purposes only. You may
                    not use the platform for unlawful purposes, commercial
                    solicitation, scraping, automated data collection, reverse
                    engineering, attempting to bypass security controls,
                    interfering with platform operation, or any activity that
                    could harm Beramour, Beta Infinity Ltd., or other users.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(5). Free Trial Periods.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Generally, Beta Infinity Ltd. offers a free trial period on
                    its dating platform service, which is provided through the
                    Beramour dating application. The free trial period is
                    intended for potential clients who wish to explore the
                    application in depth, at no cost, before making payments
                    after the expiry of this trial period. This period is
                    determined at the company’s discretion and is not subject to
                    extension upon request by users under any circumstances.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (6). Eligibility for Access, Registration or Use.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (6.1) Children's Privacy and Minimum Age: To sign up for our
                    dating platform service, you must be at least 18 years old.
                    By utilizing our dating platform service, you confirm that
                    you meet this critical age requirement. Our services are not
                    intended for individuals under the age of 18. We do not
                    knowingly collect or solicit personal information from
                    individuals under 18. If we become aware that we have
                    collected personal information from a child under 18, we
                    will take steps to delete such information with immediate
                    effect.
                    <br />
                    (6.2) Marital Status: By signing up for or utilizing our
                    dating platform service, you affirm and guarantee that you
                    are either single or, if previously legally wedded, now
                    separated. If you are married and not separated, you are
                    ineligible to register for or utilize our dating platform
                    service.
                    <br />
                    (6.3) Criminal Background: By expressing your interest in,
                    signing up for, or using our dating platform service, you
                    assert and warrant that you have no history of criminal
                    convictions or other serious offenses, including sexual
                    offenses.
                    <br />
                  </Typography>

                  {/* --- NEW: Account Security Obligations --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (7). Account Security and User Responsibilities.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You are responsible for maintaining the confidentiality of
                    your login credentials, restricting access to your devices,
                    and promptly notifying us if you suspect unauthorized access
                    or compromise. You agree not to share your password or allow
                    third parties to access your account. We may implement and
                    require reasonable security measures (including verification
                    steps) to protect accounts and the platform.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (8). Information Disclosure Under Legal Obligation.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You recognize and consent that Beta Infinity Ltd may reveal
                    any information you provide, following our Privacy Policy
                    guidelines, under legal obligation, upon the request by
                    specific external entities, or if we, at our discretion,
                    deem it necessary to comply with legal requirements,
                    requests from law enforcement, or any judicial proceedings.{" "}
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (9). Effecting Payments, Processing Fees and Implications.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    (9.1) Client-Side Payment Initiation: Whether you are
                    initiating a payment as a "Single" or "Client" to
                    Beramour/Beta Infinity for the provision of dating services,
                    or initiating a payment to yourself as an "Affiliate
                    Partner" through the Beramour affiliate module, we have
                    intentionally abstained from automating a subscription
                    service to settle these payments.
                    <br />
                    (9.2) When making a payment, you will need to provide
                    specific payment details, such as your billing account
                    information. This may include your credit or debit card,
                    mobile money number, or PayPal account number. You agree
                    that the information you provide will be truthful,
                    comprehensive, and up to date. Additionally, you agree to
                    cover all legitimate charges, including any associated
                    processing fees charged by your financial institution.
                    <br />
                    (9.3) Minimum Payment for 30 Calendar Days and Account
                    Balance: By making payments to utilize our dating platform
                    service, you will be deemed to have understood that the
                    minimum payment covers 30 calendar days. However, you may
                    pay for more days if you so wish. The system calculates the
                    total positive number of days corresponding to your payment.
                    This balance is shown once you log in. During the free trial
                    period, no payments are required, and the relevant account
                    status can be accessed after login.
                    <br />
                    (9.4) No Refund Policy on the Minimum 30 Calendar Days'
                    Payments: Considering the free trial periods under item (5)
                    and the minimum 30 calendar days' payments, we are of the
                    opinion that we provide one of the most affordable platforms
                    for dating services compared to other players in the
                    industry. In order to keep these prices low, we minimize
                    personnel and administrative costs and therefore operate
                    under a strictly{" "}
                    <strong>
                      no refund policy for the minimum 30 calendar days'
                      payments.
                    </strong>
                    If, however, you paid for more than 30 calendar days and
                    would like to request a refund, your refund can be processed
                    under the following conditions:
                    <ol type="a">
                      <li>
                        The minimum 30 calendar days' payment is deducted from
                        the total amount, commencing from the day the payment
                        was made;
                      </li>
                      <li>
                        The remaining amount will be processed and refunded;
                        processing fees will be borne by the client and deducted
                        from the balance;
                      </li>
                      <li>
                        Claims must be initiated by the client within 15 days of
                        making the initial payment. For these claims, clients
                        are advised to use the email address listed at the
                        bottom of this document.
                      </li>
                    </ol>
                    <br />
                  </Typography>

                  {/* --- NEW: Chargebacks / Fraud --- */}
                  <Typography component="dd" paragraph>
                    <strong>
                      (9.5) Chargebacks, Fraud, and Payment Abuse:
                    </strong>{" "}
                    If we reasonably suspect payment fraud, abuse of refunds, or
                    chargebacks that violate these Terms, we may suspend or
                    terminate access and/or decline future payments, subject to
                    applicable law. You agree to cooperate with reasonable
                    verification requests relating to payments and account
                    security.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(10). Prohibition of Use by Third Parties.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Your account is intended solely for your individual use. You
                    are not permitted to grant authorization for others to use
                    your account, nor can you assign or transfer your account to
                    any other individual or entity, unless expressly agreed upon
                    by us beforehand. You acknowledge that Beramour, and by
                    extension Beta Infinity Ltd., bears no responsibility for
                    access to and/or use of your account by persons other than
                    yourself, arising from theft or unauthorized use of your
                    login credentials.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (11). Accountability for Information You Provide.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You are solely accountable and responsible, within legal
                    boundaries, for:
                    <ol type="a">
                      <li>
                        The information and content you provide on the dating
                        platform;
                      </li>
                      <li>
                        The information and content you share, send, or publish
                        through our dating service platform; and
                      </li>
                      <li>
                        Your engagements with other registered users through our
                        dating service platform.
                      </li>
                    </ol>
                  </Typography>

                  {/* --- NEW: User Content License + Rights --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(12). Your Content and License to Beramour.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You retain ownership of content you submit (such as profile
                    information, photos, and messages), subject to applicable
                    law. By submitting content on Beramour, you grant Beta
                    Infinity Ltd. a worldwide, non-exclusive, royalty-free,
                    sublicensable, and transferable license to host, store,
                    reproduce, display, publish (within the platform),
                    distribute, adapt (for formatting and technical purposes),
                    and otherwise use such content solely to operate, secure,
                    improve, and enforce the platform and these Terms.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (13). Prohibited Exposure of Private and Personal Contact
                      Information.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You agree that you will <strong>NOT POST, NOR SHARE</strong>{" "}
                    any full name, phone number, address, email address,
                    personal website address or third-party profile page, or
                    other contact information you may come across regarding
                    other registered members.{" "}
                    <strong>
                      Beramour Dating Application DOES NOT expose or share your
                      real name or contact information with any other registered
                      single on its platform. In each case, you are only
                      identified through your nickname on our platform.
                    </strong>{" "}
                    You may, at your discretion, exchange such information when
                    you reach the direct communication phase with your matches.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (14). Operating at Your Own Risk and Need for Necessary
                      Precautions.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    By utilizing our services, you are assuming all associated
                    risks, whether online or offline, including those inherent
                    in interactions with others, such as physical meetings. You
                    commit to taking all essential precautions when connecting
                    with individuals through our dating service. In addition,
                    you agree to read and follow the recommendations set forth
                    on your profile page, under "Safety Tips".
                    <br />
                    While we strive to maintain a safe and secure environment,
                    we cannot guarantee the authenticity or accuracy of user
                    profiles or the information provided therein. Users are
                    encouraged to exercise caution and verify the identity and
                    intentions of individuals they interact with on our
                    platform.
                    <br />
                    We advise users to prioritize their safety and take
                    appropriate precautions when meeting someone for the first
                    time, including meeting in public places, informing a
                    trusted friend or family member of their plans, and
                    utilizing our inbuilt video and messaging system to
                    communicate and establish the authenticity of your match
                    initially. Remember, your partner never sees your real name
                    or contact details unless you choose to share them.
                    <br />
                    Beramour and/or Beta Infinity Ltd. is/are not liable in any
                    way for actions arising from your interactions with other
                    persons on our dating platform.
                    <br />
                  </Typography>

                  {/* --- NEW: No Background Checks --- */}
                  <Typography component="dd" paragraph>
                    <strong>(14.1) No Background Checks:</strong> You understand
                    that Beramour does not routinely conduct criminal background
                    checks or identity verification on users, and we do not
                    guarantee that any user is who they claim to be. You agree
                    to exercise caution and use the reporting tools if you
                    suspect misrepresentation, scams, or harmful conduct.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(15). Strictly Prohibited Content.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour, and by extension Beta Infinity Ltd., forbids
                    uploading or distribution of content that:
                    <ol type="a">
                      <li>
                        infringes the intellectual property rights of Beta
                        Infinity Ltd. or any third party, including copyrights;
                      </li>
                      <li>
                        could reasonably be considered offensive or may harass,
                        abuse, or cause psychological distress to another
                        individual;
                      </li>
                      <li>
                        is abusive, threatening, discriminatory, or promotes or
                        encourages racism, sexism, hatred, or bigotry;
                      </li>
                      <li>
                        is obscene, pornographic, violent, or contains nudity;
                      </li>
                      <li>
                        encourages or facilitates any illegal activity,
                        including (without limitation) terrorism, or
                        instructions on how to make or buy illegal weapons or
                        drugs;
                      </li>
                      <li>
                        includes an image or likeness of a minor who is
                        unaccompanied by the minor's parent or guardian, or not
                        fully clothed, or otherwise depicts or implies a minor
                        engaged in sexual activity;
                      </li>
                      <li>
                        encourages or facilitates any activity that may result
                        in harm to the user or another person, including (but
                        not limited to) promotion of self-harm, dangerous
                        challenges, violent extremism, or group harm;
                      </li>
                      <li>
                        is malicious, defamatory, libellous, untrue, false, or
                        misrepresentative, or is otherwise intended to defraud,
                        swindle, or deceive other users of the services;
                      </li>
                      <li>
                        relates to commercial activities, including (without
                        limitation) sales, competitions, promotions and
                        advertising, solicitation for services, sex work, or
                        links to other websites;
                      </li>
                      <li>
                        is generated through Artificial Intelligence and may
                        appear to distort or deceive other members through
                        misrepresentation;
                      </li>
                      <li>
                        impersonates or otherwise misrepresents affiliation,
                        connection, or association with any person or entity;
                      </li>
                      <li>involves or facilitates the transmission of spam;</li>
                      <li>
                        contains any spyware, adware, viruses, corrupt files,
                        worm programs, or other malicious code designed to
                        interrupt, damage, or limit the functionality of, or
                        disrupt, any software, hardware, telecommunications,
                        networks, servers, or other equipment.
                      </li>
                    </ol>
                    Contravention of any of these prohibitions may result in the
                    immediate suspension or deletion of your account, with no
                    refunds whatsoever.
                    <br />
                  </Typography>

                  {/* --- NEW: Harassment / Off-platform Solicitation / Circumvention --- */}
                  <Typography component="dd" paragraph>
                    <strong>
                      (15.1) Harassment, Exploitation, and Circumvention:
                    </strong>{" "}
                    You may not harass, threaten, stalk, extort, coerce,
                    exploit, or attempt to defraud any user. You may not solicit
                    money, gifts, or financial assistance from users, nor use
                    the platform to facilitate scams. You may not encourage
                    users to leave the platform for purposes that violate these
                    Terms, including evading enforcement actions or engaging in
                    illegal activity.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (16). Reporting of Violations to Beramour Administrators.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You agree that you will promptly report to Beramour
                    Administrators any violation of this agreement by others,
                    including (but not limited to) registered users. You are
                    encouraged to use the email for reporting policy violations
                    below. Your report shall be treated with utmost
                    confidentiality.
                    <br />
                  </Typography>

                  {/* --- NEW: Investigation + Cooperation --- */}
                  <Typography component="dd" paragraph>
                    <strong>(16.1) Cooperation with Investigations:</strong> You
                    agree to cooperate with reasonable requests for information
                    relating to safety investigations, abuse reports, account
                    security, payment fraud, and enforcement of these Terms,
                    subject to applicable law.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (17). Content Removal by Beramour Administrators.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour Administrators reserve the right, but not the
                    obligation, to monitor the information or material you share
                    on our platform. Beramour Administrators will have the right
                    to remove any such content if it violates, or may violate,
                    any applicable law or any policy under this Agreement.
                    <br />
                  </Typography>

                  {/* --- NEW: Platform Integrity + Moderation Discretion --- */}
                  <Typography component="dd" paragraph>
                    <strong>
                      (17.1) Moderation and Enforcement Discretion:
                    </strong>{" "}
                    We may take enforcement action at our discretion where we
                    reasonably believe there is a violation of these Terms, the
                    Community Rules, safety guidance, or applicable law. Such
                    actions may include content removal, warnings, feature
                    limitations, temporary suspensions, or permanent bans.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(18). Deactivating Your Account.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You can deactivate your account at any time you wish, at
                    your own discretion. This can be accomplished by going to
                    "My Profile", then “Settings”, then clicking on “Deactivate
                    Account” and following the instructions to the end. During
                    the period that your account remains deactivated, your
                    prepaid balance will continue being utilized until
                    depletion. No refunds or claims of used days can be lodged
                    with the company whatsoever.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(19). Voluntary Membership Cessation.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You can delete your account at any time by logging into the
                    dating application, going to "My Profile", then “Settings”,
                    then clicking "Delete Account", and following the
                    instructions to complete the deletion process. Any payments
                    made prior to cancellation for the Minimum 30 Calendar Days
                    period will not be refunded. If, however, you made a payment
                    in excess of the Minimum 30 Calendar Days, you may request a
                    refund, which shall be processed in accordance with Article
                    (9.4) of this agreement. Beramour will retain the deleted
                    data for a period of 180 calendar days, which may, upon
                    request by law enforcement or judicial authorities, be
                    disclosed at the company's discretion as described under
                    clause (8) above.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (20). Membership Termination by Beramour Administrators.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    We reserve the right to promptly terminate your membership
                    and access to the dating service at any time if we determine
                    (at our sole discretion) that you have violated this
                    agreement by contravening any of its clauses. The
                    termination of your membership will be communicated to you
                    via the email address provided during registration.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>
                      (21). Appeal Against Wrongful Membership Termination.
                    </strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    While we strive to make this platform one of the best in the
                    world through affordable yet high-value service delivery, we
                    acknowledge that errors can occur. In the unfortunate event
                    of wrongful membership termination by our administrators,
                    you may appeal to our supervisors by sending an email using
                    the reporting address provided below. The matter will be
                    given fair and impartial consideration.
                    <br />
                  </Typography>

                  {/* --- NEW: Intellectual Property --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(22). Intellectual Property Rights.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour, the application, website, trademarks, logos,
                    designs, software, and all associated content (excluding
                    user content) are owned by or licensed to Beta Infinity Ltd.
                    You may not copy, modify, distribute, sell, lease, reverse
                    engineer, or create derivative works from our intellectual
                    property except as permitted by law or with our prior
                    written consent.
                    <br />
                  </Typography>

                  {/* --- NEW: Third-Party Services / Links --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(23). Third-Party Services and Links.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    The platform may contain links to third-party websites or
                    services, or may rely on third-party providers (such as
                    payment processors). We do not control third-party services
                    and are not responsible for their content, policies, or
                    practices. Your dealings with third parties are at your own
                    risk and may be governed by their terms and policies.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(24). Limitation of Liability.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    To the fullest extent permitted by applicable law, Beta
                    Infinity Ltd., its affiliates, staff, or service providers
                    shall not be held liable for any indirect, consequential,
                    exemplary, incidental, special, punitive, fixed, or enhanced
                    damages, including but not limited to loss of profits
                    (whether incurred directly or indirectly), or any loss of
                    data, use, goodwill, or other intangible losses resulting
                    from: <br />
                    <ol type="a">
                      <li>
                        your access to or use of, or inability to access or use,
                        the services;
                      </li>
                      <li>
                        the behaviour or content of any users or third parties
                        on or through the services or in connection with our
                        services; or
                      </li>
                      <li>
                        any unauthorized access, use, or alteration of your
                        content, even if Beramour/Beta Infinity Ltd. has been
                        informed of the potential for such occurrences.
                      </li>
                    </ol>
                  </Typography>

                  {/* --- NEW: Indemnity --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(25). Indemnity.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    You agree to indemnify, defend, and hold harmless Beta
                    Infinity Ltd., its directors, employees, agents, and service
                    providers from and against any claims, liabilities, damages,
                    losses, and expenses (including reasonable legal fees)
                    arising out of or related to your use of the services, your
                    content, your interactions with other users, or your
                    violation of these Terms or applicable law.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(26). Governing Law and Jurisdiction.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour/Beta Infinity Ltd. explicitly states that the
                    applicable governing law for all matters arising from its
                    operations and services shall be the Law of the Republic of
                    Kenya. Legal cases may be filed in any court within the
                    County of Nairobi, Kenya.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(27). Disclaimer of Warranties.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour/Beta Infinity Ltd. hereby declares that it provides
                    its services on an "as is" and "as available" basis. To the
                    fullest extent permitted by law, the company disclaims all
                    warranties—whether express, implied, statutory, or
                    otherwise—regarding its services (including all content
                    therein). Such disclaimed warranties include, but are not
                    limited to, implied warranties of satisfactory quality,
                    merchantability, fitness for a particular purpose, or
                    non-infringement. The company does not assert or guarantee
                    that: <br />
                    <ol type="a">
                      <li>
                        its services will remain uninterrupted, secure, or free
                        from errors;
                      </li>
                      <li>
                        any defects or errors within its services will be
                        identified or rectified; or
                      </li>
                      <li>
                        any content or information acquired through its services
                        will be accurate, comprehensive, up-to-date, or suitable
                        for the user's intended purposes.
                      </li>
                    </ol>
                  </Typography>

                  {/* --- NEW: Severability + Entire Agreement --- */}
                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(28). Severability and Entire Agreement.</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    If any provision of these Terms is found to be unlawful,
                    void, or unenforceable, that provision shall be deemed
                    severable and shall not affect the validity and
                    enforceability of the remaining provisions. These Terms,
                    together with the Privacy Policy and Affiliate Policy,
                    constitute the entire agreement between you and Beta
                    Infinity Ltd. regarding the services.
                    <br />
                  </Typography>

                  <Typography variant="h6" component="dt" gutterBottom>
                    <strong>(29). Contact Details</strong>
                  </Typography>
                  <Typography component="dd" paragraph>
                    Beramour Dating Platform
                    <br />
                    Beta Infinity Ltd
                    <br />
                    PO Box 7129,
                    <br />
                    00200 City Square,
                    <br />
                    Nairobi, Kenya
                    <br />
                    Contact: support.beramour.com (Open a Ticket)
                    <br />
                    Beramour Dating Platform is a product of Beta Infinity Ltd.
                  </Typography>
                </Box>
              </Box>
              {/* --- Terms of Use Content Ends Here --- */}
            </CardBody>
          </Card>
        </GridItem>
      </GridContainer>
    </div>
  );
}
